/**
 * 云服务类别 产品图表页 详情Dialog
 *
 * @format
 */

import React, { useState, useEffect } from 'react'
import { _$, Dialog, Button, Select, Option } from 'kpc-react'
import styled from 'styled-components'
import QuickRangeDate from 'src/components/QuickRangeDate'
import getMetricStatisticsBatch from './getMetricStatisticsBatch'
import { IMonitorGraphData, calcSelectInterval, delayToHours } from './constants'
import BaseChart from 'src/components/BaseChart'
import { tooltipFormatter, formatInt, handleUnit, unitFormatBps, unitFormatbps } from 'src/utils'
import NoData from 'src/components/NoData'
import { minBy, maxBy } from 'lodash'

interface MonitorChartDetailsProps {
  visible: boolean
  start: number
  end: number
  graph: IMonitorGraphData
  closeExport: () => void
  className?: string
}

const MonitorChartDetails: React.FC<MonitorChartDetailsProps> = ({
  visible,
  start,
  end,
  graph,
  closeExport,
  className
}) => {
  const [dateTime, setDateTime] = useState<[number, number]>([null, null])
  const [delayOption, setDelayOption] = useState<number[]>([])
  const [delay, setDelay] = useState<number>()

  // dashboard query 接口
  const { getDashboardChart, chartData } = getMetricStatisticsBatch()

  // 初始化
  useEffect(() => {
    if (visible) {
      const delayArray = calcSelectInterval(start, end, [graph])
      setDelayOption(delayArray)
      setDelay(delayArray[0])
      setDateTime([start, end])
      getDashboardChart(start, end, delayArray[0], graph)
    }
  }, [visible])

  const handleDateTimeChange = date => {
    setDateTime(date)
    if (date[0] && date[1]) {
      const delayArray = calcSelectInterval(date[0], date[1], [graph])
      setDelayOption(delayArray)
      setDelay(delayArray[0])
      getDashboardChart(date[0], date[1], delayArray[0], graph)
    }
  }

  const handleDelayChange = value => {
    setDelay(value)
    getDashboardChart(dateTime[0], dateTime[1], value, graph)
  }

  const { unit } = React.useMemo(() => {
    if (chartData?.series?.length) {
      // 计算图表最大值最小值
      // const values: number[] = []
      // chartData?.series?.forEach(item => {
      //   const data = item.data.map(value => Number(value))
      //   values.push(...data)
      // })
      // const max = maxBy(values) as number
      // const min = minBy(values) as number
      const first = chartData.series[0].name.split('===')[1]
      if (unitFormatBps(first || '') || unitFormatbps(first || '')) {
        const bpsUnit = chartData.series.every(item => {
          const titleSplit = item?.name.split('===')[1]
          return titleSplit === first
        })
        return {
          unit: bpsUnit ? first : ''
          // yMax: max,
          // yMin: min
        }
      } else {
        return {
          unit: ''
          // yMax: max,
          // yMin: min
        }
      }
    } else {
      return {
        unit: ''
      }
    }
  }, [chartData])

  return (
    <Dialog
      className={className}
      mode="destroy"
      width={1096}
      value={visible}
      on$change-value={(_c, show) => {
        !show && closeExport()
      }}
      title={graph?.title || _$('图表详情')}
      b-footer={
        <React.Fragment>
          <Button onClick={closeExport}>{_$('关闭')}</Button>
        </React.Fragment>
      }
    >
      <div>
        <div className="date-select">
          <QuickRangeDate
            realoadIcon={false}
            format="YYYY-MM-DD HH:mm"
            optionalDateRange={90}
            dateDiff={31}
            defautlValue={dateTime}
            quickRange={['近0.5小时', '近1天', '近3天', '近1周', '近30天']}
            changeValue={handleDateTimeChange}
          />
          <div className="delay-select">
            <span>{_$('周期')}：</span>
            <Select value={delay} onChange={handleDelayChange} width={120}>
              {delayOption.length
                ? delayOption.map(item => (
                    <Option key={item} value={item}>
                      {delayToHours(item)}
                    </Option>
                  ))
                : null}
            </Select>
          </div>
        </div>
        <div className="chart-box">
          {chartData ? (
            <BaseChart
              options={{
                xAxis: {
                  type: 'category',
                  boundaryGap: false,
                  data: chartData?.xData || [],
                  splitLine: {
                    show: true,
                    lineStyle: {
                      type: 'dashed'
                    }
                  }
                },
                series: chartData?.series || [],
                yAxis: {
                  name: `${_$('周期')}:${chartData?.interval}s`,
                  // min: formatInt(yMin, false),
                  // max: formatInt(yMax),
                  type: 'value',
                  axisLine: {
                    show: true
                  },
                  axisLabel: {
                    formatter: function (value, index) {
                      return handleUnit(value, unit, true)
                    }
                  }
                },
                tooltip: {
                  trigger: 'axis',
                  enterable: true,
                  formatter: function (params) {
                    return tooltipFormatter(params)
                  }
                },
                legend: {
                  type: 'scroll',
                  right: '0',
                  width: '80%',
                  top: '-3px',
                  formatter: name => {
                    const nameSplit = name.split('===')
                    return nameSplit[0]
                  }
                },
                dataZoom:
                  chartData?.xData?.length > 20
                    ? [
                        {
                          type: 'inside',
                          start: 0,
                          end: 100
                        },
                        {
                          start: 0,
                          end: 100
                        }
                      ]
                    : [],
                grid: {
                  left: '20px',
                  right: '35px',
                  top: '30px',
                  bottom: '50px',
                  containLabel: true
                }
              }}
            />
          ) : (
            <NoData />
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default styled(MonitorChartDetails)`
  .date-select {
    display: flex;
    .delay-select {
      margin-left: 16px;
    }
  }
  .chart-box {
    margin-top: 16px;
    width: 100%;
    height: 350px;
  }
`
